import { CSSProperties, useLayoutEffect, useState } from 'react';

/**
 * Scoped loader for the public/landing stylesheets.
 *
 * Why this exists
 * ---------------
 * The landing + auth pages use third-party global stylesheets (bootstrap, the
 * marketing theme, the Muli form theme). Those files style bare element
 * selectors -- `body`, `h1..h6`, `input, textarea, select, button` -- so once
 * they are in the document they apply to the whole app.
 *
 * Two separate leaks used to result from that:
 *
 *  1. `css2/style.css` was imported statically by SigninForm / SignUpForm /
 *     ForgotPassword. Those components are reachable statically from App.tsx,
 *     so webpack extracted the file into `main.css` -- it was on every page of
 *     the authenticated app, permanently. That is what put `Muli-Regular`
 *     (13px, #333) on every native input/select/textarea/button, including the
 *     ones inside MUI Dialogs and Menus, which render into a portal on
 *     `document.body` and therefore sit outside the app's DOM subtree.
 *
 *  2. The landing pages `import()`ed the other five files from a `useEffect`.
 *     Webpack injects those chunks into <head> and never removes them, so
 *     after a client-side navigation from /signin into the dashboard the
 *     bootstrap reset and the Lato/Poppins rules stayed in the cascade. A
 *     full page reload of a dashboard URL never loads those chunks, which is
 *     why a refresh "fixed" the fonts and why the problem came back on the
 *     next login.
 *
 * The fix is to own the lifetime of these stylesheets: load them on mount,
 * and set `disabled = true` on the exact <style>/<link> nodes they injected
 * when the last landing page unmounts. Re-mounting flips them back on, which
 * costs nothing because the chunk is already cached.
 *
 * They are treated as one indivisible bundle. Ownership of the injected nodes
 * is worked out by diffing <head> around the import, which is only
 * unambiguous if nothing else of ours is loading at the same time -- a single
 * bundle guarantees that while still letting the six files load in parallel.
 * Every component that needs any of them renders inside one of the six pages
 * that request the bundle, so there is no need for finer granularity.
 */

type SheetNode = HTMLStyleElement | HTMLLinkElement;

const LOADERS = [
  () => import('./css/animate.css'),
  () => import('./css/glightbox.min.css'),
  () => import('./css/lineicons.css'),
  () => import('./css/bootstrap.min.css'),
  () => import('./css/style.css'),
  () => import('./css2/style.css'),
];

const SHEET_SELECTOR = 'style, link[rel="stylesheet"]';

/** Nodes the bundle injected, so we can toggle exactly those back off. */
let injected: SheetNode[] | null = null;
/** In-flight (or settled) load, so concurrent mounts share one request. */
let loading: Promise<void> | null = null;
/** How many mounted components currently need the bundle. */
let refCount = 0;

/**
 * Emotion (MUI) also writes <style> tags into <head> at arbitrary times.
 * Those carry a `data-emotion` attribute; never claim ownership of them.
 */
function ownedByUs(node: Element): node is SheetNode {
  if (node.tagName === 'LINK') return true;
  return node.tagName === 'STYLE' && !node.hasAttribute('data-emotion');
}

function setDisabled(disabled: boolean) {
  if (!injected) return;
  injected.forEach((node) => {
    node.disabled = disabled;
  });
}

function load(): Promise<void> {
  if (loading) return loading;

  const before = new Set(Array.from(document.querySelectorAll(SHEET_SELECTOR)));
  loading = Promise.all(LOADERS.map((open) => open())).then(() => {
    injected = Array.from(document.querySelectorAll(SHEET_SELECTOR)).filter(
      (node) => !before.has(node) && ownedByUs(node)
    ) as SheetNode[];
    // The page may have unmounted while the chunks were in flight.
    if (refCount === 0) setDisabled(true);
  });

  return loading;
}

/**
 * Keeps the landing stylesheets active for as long as the calling component
 * is mounted, and deactivates them once nothing needs them.
 *
 * Returns whether the stylesheets are applied. Landing pages should keep
 * their preloader up until it is true -- otherwise the first paint happens
 * against unstyled markup, since even the `.preloader` overlay itself is
 * styled by `css/style.css`.
 */
export function useLandingStyles(): boolean {
  const [ready, setReady] = useState(() => injected !== null);

  // Layout effect, not a plain effect: on a re-visit the stylesheets are
  // already fetched and re-enabling them is synchronous, so doing it before
  // the browser paints avoids a flash of unstyled content.
  useLayoutEffect(() => {
    refCount += 1;
    setDisabled(false);

    let active = true;
    void load().then(() => {
      if (refCount > 0) setDisabled(false);
      if (active) setReady(true);
    });

    return () => {
      active = false;
      refCount -= 1;
      if (refCount <= 0) setDisabled(true);
    };
  }, []);

  return ready;
}

/**
 * Inline copy of the `.preloader` overlay rules from `css/style.css`, so the
 * overlay covers the page even on the very first paint, before that
 * stylesheet has loaded.
 */
export const PRELOADER_FALLBACK_STYLE: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  display: 'table',
  height: '100%',
  width: '100%',
  background: '#fff',
  zIndex: 99999,
};
